import { TextareaField } from "@exopengithub/exokit";
import { Button } from "@exopengithub/exokit/v2";
import {
	showErrorNotification,
	showSuccessNotification,
} from "molecules/notifications/notificationEvents";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import { AI_PRODUCT_NAME } from "src/common/branding";
import { useLoadedCompanyDomain } from "src/routes/routeDataHooks";
import {
	useGetCompanyDomainSettingsQuery,
	useUpdateCompanyDomainSettingsMutation,
} from "../ai-chat/api/aiChatApi";
import { Card } from "../planning/components/Card";
import { AiSkillsSection } from "./skills/AiSkillsSection";

function autoResize(textarea: HTMLTextAreaElement | null) {
	if (!textarea) return;
	textarea.style.height = "auto";
	textarea.style.height = `${textarea.scrollHeight}px`;
}

export const AiChatSettingsPage = () => {
	const { t } = useTranslation();
	const companyDomain = useLoadedCompanyDomain();
	const { data: settings } = useGetCompanyDomainSettingsQuery(companyDomain.id);
	const [updateCompanyDomainSettings, { isLoading: isSaving }] =
		useUpdateCompanyDomainSettingsMutation();

	const [localPreferences, setLocalPreferences] = useState<string | null>(null);

	const currentPreferences = settings?.companyPreferences ?? "";
	const displayedPreferences =
		localPreferences !== null ? localPreferences : currentPreferences;
	const isDirty =
		localPreferences !== null && localPreferences !== currentPreferences;

	const textareaRef = useCallback((el: HTMLTextAreaElement | null) => {
		autoResize(el);
	}, []);

	const handleSave = async () => {
		try {
			await updateCompanyDomainSettings({
				companyDomainId: companyDomain.id,
				companyPreferences: displayedPreferences || null,
			}).unwrap();
			showSuccessNotification({ message: t("Settings saved") });
			setLocalPreferences(null);
		} catch {
			showErrorNotification({
				message: t("Failed to save settings"),
			});
		}
	};

	return (
		<>
			<Card>
				<div className="p-4">
					<div className="mb-2 font-bold text-lg">{AI_PRODUCT_NAME}</div>
					<TextareaField
						ref={textareaRef}
						label={t("Company preferences")}
						hint={t(
							"These preferences are provided to the AI as context for all users in this company domain",
						)}
						placeholder={t(
							"e.g. We use K3 accounting standards, our fiscal year ends in June",
						)}
						value={displayedPreferences}
						onChange={(e) => {
							setLocalPreferences(e.target.value);
							autoResize(e.target);
						}}
						rows={4}
						resizable={false}
					/>
					<div className="flex justify-end mt-4">
						<Button onClick={handleSave} disabled={!isDirty || isSaving}>
							{t("Save")}
						</Button>
					</div>
				</div>
			</Card>
			<AiSkillsSection companyDomainId={companyDomain.id} />
		</>
	);
};
